/**
 * AdminDashboardKPIs — hero KPI cards + recent activity + expiring trials
 * for the admin Dashboard tab. Replaces the old inline stat cards with a
 * professional card set matching the new admin shell.
 */
import React from 'react';
import {
  Building2, CheckCircle2, DollarSign, TrendingUp, Sparkles, AlertTriangle,
  ArrowUpRight, Users
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, tone = 'blue', accent }) {
  const tones = {
    blue:    { ring: 'ring-blue-100',    dot: 'bg-blue-500',    iconBg: 'bg-blue-50 text-blue-600' },
    emerald: { ring: 'ring-emerald-100', dot: 'bg-emerald-500', iconBg: 'bg-emerald-50 text-emerald-600' },
    amber:   { ring: 'ring-amber-100',   dot: 'bg-amber-500',   iconBg: 'bg-amber-50 text-amber-600' },
    purple:  { ring: 'ring-purple-100',  dot: 'bg-purple-500',  iconBg: 'bg-purple-50 text-purple-600' },
    rose:    { ring: 'ring-rose-100',    dot: 'bg-rose-500',    iconBg: 'bg-rose-50 text-rose-600' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div className={`bg-white rounded-2xl border border-slate-200 p-5 shadow-sm ring-4 ${t.ring} transition-shadow hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${t.iconBg}`}>
          <Icon className="h-5 w-5" />
        </div>
        {accent && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
            <ArrowUpRight className="h-3 w-3" /> {accent}
          </span>
        )}
      </div>
      <div className="mt-4">
        <div className="text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
        <div className="text-sm font-semibold text-slate-700 mt-1">{label}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function statusBadge(status) {
  const map = {
    active:   'bg-emerald-100 text-emerald-800 border-emerald-200',
    trial:    'bg-blue-100 text-blue-800 border-blue-200',
    beta:     'bg-purple-100 text-purple-800 border-purple-200',
    inactive: 'bg-slate-100 text-slate-700 border-slate-200',
    cancelled:'bg-rose-100 text-rose-800 border-rose-200',
  };
  const cls = map[status] || map.inactive;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {status || 'unknown'}
    </span>
  );
}

export default function AdminDashboardKPIs({ dashboardData, onExtendTrial }) {
  const stats = dashboardData?.stats || {};
  const recent = dashboardData?.recent_practices || [];
  const expiring = dashboardData?.expiring_trials || [];

  const activeCount = stats.active_practices || 0;
  const totalCount = stats.total_practices || 0;
  const trialCount = Math.max(0, totalCount - activeCount);
  const activePct = totalCount ? Math.round((activeCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Welcome strip */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Welcome back 👋</h1>
            <p className="text-blue-100 mt-1 text-sm">Here's what's happening across PractCom today.</p>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-blue-200">Active Rate</div>
              <div className="text-2xl font-bold">{activePct}%</div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-white/30" />
            <div>
              <div className="text-xs uppercase tracking-wider text-blue-200">Monthly Rev</div>
              <div className="text-2xl font-bold">${(stats.monthly_revenue || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          label="Total Practices"
          value={totalCount.toLocaleString()}
          sub="All registered accounts"
          tone="blue"
        />
        <StatCard
          icon={CheckCircle2}
          label="Active Practices"
          value={activeCount.toLocaleString()}
          sub={`${activePct}% of total`}
          tone="emerald"
        />
        <StatCard
          icon={Users}
          label="Trial Accounts"
          value={trialCount.toLocaleString()}
          sub="Currently in trial"
          tone="amber"
        />
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={`$${(stats.total_revenue || 0).toLocaleString()}`}
          sub="All-time earnings"
          tone="purple"
        />
      </div>

      {/* Recent + Expiring */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-blue-600" /> Recent Practices
            </h3>
            <span className="text-xs text-slate-500">{recent.length} shown</span>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">No recent practices yet.</div>
            )}
            {recent.slice(0, 5).map(p => (
              <div key={p.id} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500 truncate">{p.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.is_beta_tester && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-purple-100 text-purple-800 border-purple-200">🧪 Beta</span>
                  )}
                  {statusBadge(p.subscription?.status)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Expiring Trials
            </h3>
            <span className="text-xs text-slate-500">{expiring.length} soon</span>
          </div>
          <div className="divide-y divide-slate-100">
            {expiring.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">No trials expiring in the next 7 days. 🎉</div>
            )}
            {expiring.slice(0, 5).map(p => (
              <div key={p.id} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">Trial expires soon</div>
                </div>
                <button
                  onClick={() => onExtendTrial?.(p.id)}
                  disabled={!onExtendTrial}
                  className={`text-xs font-semibold px-3 py-1.5 text-white rounded-lg ${
                    onExtendTrial ? 'bg-amber-500 hover:bg-amber-600' : 'bg-slate-300 cursor-not-allowed'
                  }`}
                  data-testid={`admin-extend-trial-${p.id}`}
                >
                  {onExtendTrial ? 'Extend' : 'View'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
